import { mediaList } from "@/mocks/media";
import { newsList } from "@/mocks/news";
import type { MediaTitle, NewsItem } from "@/types/media";

export const movies: MediaTitle[] = mediaList.filter((m) => m.type === "movie");
export const tvShows: MediaTitle[] = mediaList.filter((m) => m.type === "tv");

export const allGenres: string[] = Array.from(
  new Set(mediaList.flatMap((m) => m.genres)),
);

export const allRegions: string[] = ["日韩", "欧美"];

export const allYears: number[] = Array.from(
  new Set([...mediaList.map((m) => m.year), ...mediaList.map((m) => m.endYear || 0)]),
)
  .filter((y) => y > 0)
  .sort((a, b) => b - a);

export function getMediaById(id: string): MediaTitle | undefined {
  return mediaList.find((m) => m.id === id);
}

export function getNewsById(id: string): NewsItem | undefined {
  return newsList.find((n) => n.id === id);
}

export function getFeatured(): MediaTitle[] {
  return mediaList.filter((m) => m.featured);
}

export function getLatest(limit = 10): MediaTitle[] {
  return [...mediaList].sort((a, b) => b.year - a.year).slice(0, limit);
}

export function getHot(limit = 6): MediaTitle[] {
  return [...mediaList].sort((a, b) => b.heat - a.heat).slice(0, limit);
}

export function getRelated(item: MediaTitle, limit = 4): MediaTitle[] {
  return mediaList
    .filter((m) => m.id !== item.id)
    .map((m) => {
      let score = 0;
      if (m.type === item.type) score += 2;
      if (m.region === item.region) score += 1;
      score += m.genres.filter((g) => item.genres.includes(g)).length * 3;
      score += m.tags.filter((t) => item.tags.includes(t)).length * 2;
      return { m, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((x) => x.m);
}

export function searchAll(query: string): { titles: MediaTitle[]; news: NewsItem[] } {
  const q = query.trim().toLowerCase();
  if (!q) return { titles: [], news: [] };
  const match = (text: string) => text.toLowerCase().includes(q);
  const titles = mediaList.filter(
    (m) =>
      match(m.title) ||
      match(m.originalTitle) ||
      match(m.director) ||
      m.cast.some((c) => match(c)) ||
      m.genres.some((g) => match(g)) ||
      m.tags.some((t) => match(t)),
  );
  const news = newsList.filter(
    (n) => match(n.title) || match(n.summary) || n.tags.some((t) => match(t)),
  );
  return { titles, news };
}

export function formatHeat(heat: number): string {
  if (heat >= 10000) return `${(heat / 10000).toFixed(1)}万`;
  return `${heat}`;
}

export interface FilterOptions {
  genres: string[];
  regions: string[];
  year: "all" | number;
  status: "all" | "连载" | "完结";
  sort: "latest" | "hot" | "rating";
}

export function filterTitles(type: "movie" | "tv", options: FilterOptions): MediaTitle[] {
  let list = mediaList.filter((m) => m.type === type);
  if (options.genres.length > 0) {
    list = list.filter((m) => m.genres.some((g) => options.genres.includes(g)));
  }
  if (options.regions.length > 0) {
    list = list.filter((m) => options.regions.includes(m.region));
  }
  if (options.year !== "all") {
    list = list.filter((m) => m.year === options.year || m.endYear === options.year);
  }
  if (type === "tv" && options.status !== "all") {
    list = list.filter((m) => m.status === options.status);
  }
  if (options.sort === "hot") {
    list = [...list].sort((a, b) => b.heat - a.heat);
  } else if (options.sort === "rating") {
    list = [...list].sort((a, b) => b.rating - a.rating);
  } else {
    list = [...list].sort((a, b) => b.year - a.year);
  }
  return list;
}

export const defaultFilterOptions: FilterOptions = {
  genres: [],
  regions: [],
  year: "all",
  status: "all",
  sort: "latest",
};